<template>
  <n-drawer
    v-model="visible"
    :class="['mate-micro-drawer', `mate-micro-drawer--${drawerModeRef}`]"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    :close-button="!!drawerPropsRef.title && drawerPropsRef.showCloseBtn"
    :primary-button="!drawerPropsRef.disablePrimaryButton"
    :secondary-button="!drawerPropsRef.disableSecondaryButton"
    :primary-text="drawerPropsRef.primaryText || '确定'"
    :secondary-text="drawerPropsRef.primaryText || '取消'"
    :title="drawerPropsRef.title"
    :mask-close="!drawerPropsRef.disableMaskClose"
    :size="drawerSize"
    @close="onClose"
  >
    <div class="tit-wrap-fold">
      <div
        class="fold-button"
        :class="[drawerIsUnfold ? 'fold-button-none' : '']"
        @click="onTrigger"
      >
        <img
          class="fold-button-img"
          :src="drawerIsUnfold ? iconAdjustRightFilled : iconAdjustLeftFilled"
        />
      </div>
    </div>
    <n-icon
      name="close"
      class="tit-drawer-close"
      @click="onClose()"
      v-show="!drawerPropsRef.title && drawerPropsRef.showCloseBtn"
    />
    <div
      ref="refDom"
      v-if="drawerModeRef === 'micro-app'"
      class="micro-container"
    >
      <MateEmpty status="loading" />
    </div>
    <iframe
      v-if="drawerModeRef === 'iframe' && iframeSrc && visible"
      ref="iframe"
      name="appIframe"
      :src="iframeSrc"
      width="100%"
      height="100%"
      frameborder="no"
      border="0"
      marginwidth="0"
      marginheight="0"
      scrolling="auto"
      allowtransparency="true"
    ></iframe>
    <component
      v-if="drawerModeRef === 'component' && drawerOpenOptions.params"
      :is="COMPONENT_MAP[drawerOpenOptions.path]"
      v-bind="drawerOpenOptions.params"
    />
  </n-drawer>
</template>
<script setup lang="ts" name="MicroDrawer">
import { ref, defineExpose, computed, nextTick } from 'vue';
import { $message, NDrawer } from '@nio-fe/lego';
import { NIcon } from '@nio-fe/icon';
import iconAdjustLeftFilled from '@/assets/images/icon_adjust_left_filled.png';
import iconAdjustRightFilled from '@/assets/images/icon_adjust_right_filled.png';
import { setGlobalState } from '@/utils/micro/index';
import useMatestoreStore from '@/store/matestoreStore';
import { getRouterName } from '@/utils/router';
import MateEmpty from '@/components/MateEmpty.vue';
import { asyncVueComponent } from '@nio-fe/mate-lib-loader';
import useGlobalStore from '@/store/globalStore';
type DrawerMode = 'iframe' | 'micro-app' | 'component';
type OpenDrawerOptions = {
  appCode?: string;
  pageCode?: string;
  appName?: string;
  path?: string;
  params?: Record<string, any>;
  drawerProps?: DrawerPropsOption;
  openAsIframe?: boolean;
};

interface DrawerPropsOption {
  disablePrimaryButton?: boolean;
  disableSecondaryButton?: boolean;
  disableMaskClose?: boolean;
  primaryText?: string;
  secondaryText?: string;
  unfold?: boolean;
  showCloseBtn?: boolean;
  title?: string;
}

const globalStore = useGlobalStore();

const UserBook = asyncVueComponent('@nio-wad/ladybird', '0', 'TitUserBook');
const VehicleBook = asyncVueComponent(
  '@nio-wad/ladybird',
  '0',
  'TitVehicleBook'
);

const COMPONENT_MAP = {
  '/user/detail': UserBook,
  '/vehicle/detail': VehicleBook,
};

const userDetailStupidPatch = (options: OpenDrawerOptions) => {
  const isUserDetail = options.path === '/user/detail';
  const hasUserBookAuthority = globalStore.hasAuthority('user_detail_normal');
  const hasOpenLeadsDetailAuthority =
    globalStore.hasAuthority('user_detail_leads');
  if (!isUserDetail) {
    return options;
  }
  if (hasOpenLeadsDetailAuthority && !hasUserBookAuthority) {
    return {
      path: `/leads#/detail/${options.params?.userCode}${
        options?.params?.brand ? `?brand=${options.params?.brand}` : ''
      }`,
      openAsIframe: true,
      drawerProps: options.drawerProps,
    };
  } else if (!hasUserBookAuthority && !hasOpenLeadsDetailAuthority) {
    $message.error('您没有权限查看该用户的详细信息');
    throw new Error('您没有权限查看该用户的详细信息');
  }
  return options;
};

const matestoreStore = useMatestoreStore();
const drawerOpenOptions = ref<Partial<OpenDrawerOptions>>({});
const visible = ref<boolean>(false);
const drawerModeRef = ref<DrawerMode>('micro-app');
const drawerIsUnfold = ref<boolean>(false);
const appNameRef = ref<string>('');
const iframeSrc = ref<string | undefined>('');
const refDom = ref(null);

const drawerPropsRef = computed(() => {
  return drawerOpenOptions.value.drawerProps || {};
});

const params = computed(() => {
  return drawerOpenOptions.value.params || {};
});

const getAppName = () => {
  let { appCode, appName, pageCode } = drawerOpenOptions.value;
  if (appCode && !appName) {
    appName = getRouterName({ appCode, pageCode });
  }
  if (!appCode && !appName) {
    return '';
  }
  return appName;
};

const appShouldUseIframeMode = computed(() => {
  const { openAsIframe } = drawerOpenOptions.value;
  if (openAsIframe) {
    return openAsIframe;
  }
  const appName = getAppName();
  const microData = matestoreStore.microMap[appName as string];
  if (!microData) {
    return false;
  }
  return microData?.isIframe;
});

const initIframeSrc = () => {
  let { path, params } = drawerOpenOptions.value;

  if (path) {
    path = path.replace(window.location.origin, '');
    if (/https:\/\//.test(path)) {
      iframeSrc.value = path;
    } else {
      if (!path.includes('/iframe-container')) {
        path = `/iframe-container${path}`;
      }
      iframeSrc.value = `${window.location.origin}${path}`;
    }
  } else {
    const appName = getAppName();
    const microData = matestoreStore.microMap[appName as string];
    if (!microData) {
      return;
    }
    if (!microData?.isIframe) {
      return;
    }
    let entry = microData.entry;
    const p = params || {};
    if (p && entry) {
      Object.keys(p).forEach((key) => {
        entry = entry?.replace(`{${key}}`, p[key]);
      });
    }
    iframeSrc.value = entry;
  }
};

function openDrawer(options: OpenDrawerOptions) {
  options = userDetailStupidPatch(options);
  let { drawerProps } = options;
  drawerOpenOptions.value = options;

  iframeSrc.value = '';

  if (!!COMPONENT_MAP[drawerOpenOptions.value.path as string]) {
    drawerModeRef.value = 'component';
  } else if (appShouldUseIframeMode.value) {
    drawerModeRef.value = 'iframe';
  } else {
    drawerModeRef.value = 'micro-app';
  }
  if (drawerModeRef.value !== 'component') {
    drawerIsUnfold.value = !!drawerProps?.unfold;
  } else {
    drawerIsUnfold.value = false;
  }
  visible.value = true;
  nextTick(() => {
    if (drawerModeRef.value === 'micro-app') {
      openAsMicroApp();
    } else if (drawerModeRef.value === 'iframe') {
      initIframeSrc();
    }
  });
}

function openAsMicroApp() {
  let { appName, pageCode, appCode } = drawerOpenOptions.value;
  if (appCode && !appName) {
    appName = getRouterName({ appCode, pageCode });
  }
  if (!appCode && !appName) {
    throw new Error(`appCode or appName is required!`);
  }

  if (appNameRef.value && appNameRef.value !== appName) {
    matestoreStore.deactivateMicroApp(appNameRef.value);
  }
  appNameRef.value = appName || '';

  const microApp = matestoreStore.microMap[appNameRef.value];
  if (!microApp.mounted) {
    initMicroApp();
  } else {
    setGlobalState({ name: appNameRef.value, ...params.value });
  }
}

async function initMicroApp() {
  const microData = matestoreStore.microMap[appNameRef.value];
  if (microData?.isIframe) {
    return;
  }
  if (!refDom.value) {
    return;
  }
  const microApp = await matestoreStore.activateMicroApp(
    appNameRef.value,
    refDom.value,
    true
  );
  if (!microApp) {
    return;
  }
  await microApp._microAppInfo.mountPromise.then();
  setGlobalState({ name: appNameRef.value, ...params.value });
  console.log(
    `%c mountPromise: ${appNameRef.value}: ${JSON.stringify(params.value)}`,
    'color: blue'
  );
}

function onClose(isUnmount?: boolean) {
  const microData = matestoreStore.microMap[appNameRef.value];
  visible.value = false;
  iframeSrc.value = '';
  if (!isUnmount && microData && !microData.isIframe) {
    matestoreStore.deactivateMicroApp(appNameRef.value);
  }
}
function onTrigger() {
  drawerIsUnfold.value = !drawerIsUnfold.value;
}
// 展开
function onUnfold() {
  drawerIsUnfold.value = true;
}
// 折叠
function onFold() {
  drawerIsUnfold.value = false;
}

function getVisible() {
  return visible.value;
}

const drawerSize = computed(() => {
  if (drawerIsUnfold.value) {
    return 'calc(100% - 84px)';
  } else {
    return 414;
  }
});

defineExpose({ openDrawer, onClose, onUnfold, onFold, getVisible });
</script>
<style lang="scss">
.mate-micro-drawer {
  z-index: 1001 !important;
  &.tit-drawer {
    overflow: inherit;
  }
  .n-drawer__mask {
    z-index: 1002 !important;
  }
  .n-drawer__container {
    z-index: 1003 !important;
  }
}

.mate-micro-drawer--component {
  .n-drawer__body {
    padding: 0;
  }
}
</style>
<style lang="scss" scoped>
.tit-wrap-fold {
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  left: -16px;
  width: 16px;
  height: 100%;
  &:hover {
    .fold-button {
      &.fold-button-none {
        display: flex;
      }
      &:hover {
        background: #e9eaec;
      }
    }
  }
  .fold-button {
    display: flex;
    width: 16px;
    height: 64px;
    background: #f4f5f7;
    border-radius: 8px 0px 0px 8px;
    flex-direction: column;
    justify-content: space-evenly;
    cursor: pointer;
    background: #f4f5f7;
    &.fold-button-none {
      display: none;
    }
    .fold-button-img {
      width: 16px;
      height: 16px;
    }
  }
}
.tit-drawer-close {
  position: absolute;
  top: 20px;
  right: 26px;
  width: 12px;
  height: 12px;
  z-index: 1003;
}
.micro-container {
  height: 100%;
  :deep(> div) {
    height: 100%;
  }
}
</style>
